import {
  createComponent,
  EmphasizedText,
  StrongText,
  Paragraph,
  Link as VisageLink,
  ExtractVisageComponentProps,
} from '@byteclaw/visage';
import Link from 'next/link';
import Image from 'next/image';
import React from 'react';
import { AuthorsList, AuthorsListContext } from './components';
import { ImageAsset } from './loaders/ImageLoader';
import { generateImageSizes } from './helpers/generateImageSizes';

const blockquote = createComponent('blockquote', {
  styles: {
    position: 'relative',
    fontStyle: 'italic',
    fontWeight: 'bold',
    fontSize: 0,
    lineHeight: 0,
    overflowWrap: 'break-word',
    px: 0,
    py: 0,
    mx: '2rem',
    my: '2rem',
    '&::before': {
      content: '"\u201C"',
      position: 'absolute',
      top: '0.1em',
      left: '-0.15em',
      color: 'color(shadesText tint(90%))',
      fontSize: '6em',
    },
    '& *': {
      position: 'relative',
      zIndex: 1,
    },
  },
});

const del = createComponent('del', {
  styles: {
    textDecoration: 'line-through',
  },
});

const li = createComponent('li', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    '& > *': {
      ml: 2,
    },
    '& :last-child': {
      mb: '2rem',
    },
  },
});

const ul = createComponent('ul', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    pl: 4,
    listStyle: 'circle outsde',
    mb: '2rem',
  },
});

const ol = createComponent('ol', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    pl: 4,
    listStyle: 'decimal outside',
    mb: '2rem',
  },
});

const h1 = createComponent('h1', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    fontSize: 5,
    lineHeight: '1.1em',
    mb: '1rem',
    mt: '3rem',
  },
});

const h2 = createComponent('h2', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    fontSize: 4,
    lineHeight: '1.1em',
    mb: '1rem',
    mt: '3rem',
  },
});

const h3 = createComponent('h3', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    fontSize: 3,
    lineHeight: '1.1em',
    mb: '1rem',
    mt: '2rem',
  },
});

const h4 = createComponent('h4', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    fontSize: 2,
    lineHeight: '1.1em',
    mb: '1rem',
    mt: '2rem',
  },
});

const h5 = createComponent('h5', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    fontSize: 1,
    lineHeight: '1.1em',
    mb: '1rem',
    mt: '2rem',
  },
});

const h6 = createComponent('h6', {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    fontSize: 1,
    lineHeight: '1.1em',
    mb: '1rem',
    mt: '2rem',
  },
});

const P = createComponent(Paragraph, {
  styles: {
    mx: 0,
    my: 0,
    px: 0,
    py: 0,
    mb: '2rem',
  },
});

const MarkdownLink = createComponent(VisageLink, {
  displayName: 'MarkdownLink',
  styles: {
    overflowWrap: 'anywhere',
  },
});

const Figure = createComponent('figure', {
  styles: {
    display: 'block',
    position: 'relative',
    m: 0,
    p: 0,
    textAlign: 'center',
    '& > figcaption': {
      color: 'color(shadesText tint(30%))',
      fontFamily: 'body',
      fontSize: -1,
      lineHeight: '1.2em',
      py: 1,
      px: 2,
    },
  },
});

const ArticleFigure: React.FC<{
  src: string;
  asset: null | ImageAsset;
  alt?: string;
  title?: string;
}> = React.memo(({ asset, src, alt, title }) => {
  return (
    <Figure>
      <Image
        src={src}
        alt={alt}
        layout="responsive"
        height={asset?.height ?? 500}
        width={asset?.width ?? 680}
        objectFit="contain"
        sizes={generateImageSizes(asset?.width ?? 680)}
      />
      {title ? <figcaption>{title}</figcaption> : null}
    </Figure>
  );
});

type VisageLinkProps = ExtractVisageComponentProps<typeof VisageLink>;

interface LinkProps
  extends Pick<VisageLinkProps, 'children' | 'styles' | 'rel' | 'target'> {
  href: string;
}

export const mdxComponents = {
  AuthorsList,
  AuthorsListContextProvider: AuthorsListContext.Provider,
  ArticleFigure,
  blockquote,
  em: EmphasizedText,
  del,
  strong: StrongText,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  li,
  a: ({ href = '/', rel, ...rest }: LinkProps) => {
    if (href.startsWith('/') || href.includes('://ekozurnal.sk')) {
      return (
        <Link href={href.replace(/^https?:\/\/ekozurnal\.sk/, '')} passHref>
          <MarkdownLink as={Link} rel={rel} {...rest} />
        </Link>
      );
    }

    return (
      <MarkdownLink
        {...rest}
        href={href}
        rel="noopener noreferrer nofollow"
        target="_blank"
      />
    );
  },
  ol,
  p: P,
  ul,
};
